import React, { useState, useEffect } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        // Try to get dedicated testimonials first
        const response = await axios.get(`${API_URL}/api/testimonials`);
        if (response.data && response.data.length > 0) {
          setTestimonials(response.data);
        } else {
          // Fallback: get honorees with quotes as testimonials
          const honoreesResponse = await axios.get(`${API_URL}/api/honorees`);
          const honoreesWithQuotes = honoreesResponse.data.filter(h => h.quote && h.quote.length > 0);
          setTestimonials(honoreesWithQuotes);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching testimonials:', err);
        setError('Failed to load testimonials. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-white">What People Are </span>
            <span className="text-amber-500">Saying</span>
          </h1>
          <p className="text-gray-300 text-lg">
            Hear from our LLAP Award recipients about what the recognition means to them and the impact of Leonard Nimoy's legacy.
          </p>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-24 bg-[#0a0a0b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              <span className="ml-3 text-gray-400">Loading testimonials...</span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-16">
              <p className="text-red-400 text-lg">{error}</p>
            </div>
          )}

          {/* Testimonials Grid */}
          {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-[#141416] border border-gray-800/50 rounded-2xl p-8 hover:border-amber-500/30 transition-all"
              >
                {/* Quote Mark */}
                <div className="text-amber-500 text-4xl font-serif mb-4">"</div>
                
                {/* Quote Text */}
                <p className="text-gray-300 leading-relaxed mb-6">
                  {testimonial.quote}
                </p>
                
                {/* Author Info */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 bg-gray-800">
                    <img
                      src={testimonial.image}
                      alt={`${testimonial.name} - Testimonial`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{testimonial.name}</h4>
                    <p className="text-gray-500 text-sm">{testimonial.title}</p>
                    {testimonial.organization && (
                      <p className="text-amber-500/70 text-sm">{testimonial.organization}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* LLAP Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-6">🖖</div>
          <h2 className="text-3xl font-bold text-white mb-6">
            Live Long and Prosper
          </h2>
          <p className="text-gray-400 leading-relaxed">
            The LLAP Tribute Award continues to honor those who embody Leonard Nimoy's message of hope, logic, and compassion. Each recipient carries forward his legacy of making the world a better place.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestimonialsPage;
